import { useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Box, Container, Typography, Stack, Alert, CircularProgress, Button, IconButton, Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import RefreshIcon from '@mui/icons-material/Refresh';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import LanguageToggle from '@/components/LanguageToggle';
import { userApi } from '@/services/api';

/**
 * The client's own analysis report. Fetches the bot-rendered HTML and shows it
 * in an iframe; while it's still being produced, shows a "please be patient"
 * screen that auto-refreshes. Tiny PDF button prints the report (Save as PDF).
 */
export default function ReportPage() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';
  const navigate = useNavigate();
  const { analysisId } = useParams();
  const iframeRef = useRef(null);

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['analysis-report', analysisId],
    queryFn: () => userApi.getReport(analysisId),
    refetchInterval: (q) => (q.state.data?.status === 'ready' ? false : 6000),
  });

  const status = data?.status;

  function downloadPdf() {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    win.focus();
    win.print();
  }

  function handleBack() {
    if (window.history.length > 1) navigate(-1);
    else navigate('/service-history');
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton size="small" onClick={handleBack} aria-label={t('common.back')}>
            {isRtl ? <ArrowForwardIcon /> : <ArrowBackIcon />}
          </IconButton>
          <Typography variant="h6" component="h1" fontWeight={700}>{t('report.title')}</Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          {status === 'ready' && (
            <Tooltip title={t('report.download')}>
              <IconButton size="small" onClick={downloadPdf} aria-label={t('report.download')}>
                <PictureAsPdfIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <LanguageToggle />
        </Stack>
      </Stack>

      {isLoading && (
        <Box sx={{ flexGrow: 1, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>
      )}

      {!isLoading && isError && (
        <Container maxWidth="sm" sx={{ py: 4 }}>
          <Alert severity="error">{t('report.notFound')}</Alert>
        </Container>
      )}

      {!isLoading && !isError && status === 'ready' && (
        <Box sx={{ flexGrow: 1, display: 'flex' }}>
          <iframe
            ref={iframeRef}
            title={t('report.title')}
            srcDoc={data.html}
            style={{ border: 'none', width: '100%', height: 'calc(100vh - 64px)' }}
          />
        </Box>
      )}

      {!isLoading && !isError && status && status !== 'ready' && (
        <Container maxWidth="sm" sx={{ py: 6 }}>
          <Stack spacing={2.5} alignItems="center" textAlign="center">
            <HourglassEmptyIcon sx={{ fontSize: 56, color: 'primary.main' }} />
            <Typography variant="h6" fontWeight={800}>{t('report.processingTitle')}</Typography>
            <Typography color="text.secondary">{t('report.processingMessage')}</Typography>
            <Stack direction="row" spacing={1} alignItems="center" color="text.secondary">
              <CircularProgress size={16} />
              <Typography variant="caption">{t('report.processingHint')}</Typography>
            </Stack>
            <Button startIcon={<RefreshIcon />} onClick={() => refetch()} disabled={isFetching}>
              {t('report.refresh')}
            </Button>
          </Stack>
        </Container>
      )}
    </Box>
  );
}
